import { useState, useEffect } from 'react';
import { MessageSquare, Send, Mail, CreditCard, Layout, Lightbulb, Bug, HelpCircle } from 'lucide-react';
import { Button, Card, CardHeader, TextArea, Alert, LoadingSpinner, Input } from '@/components/ui';
import { StarRating } from '@/components/ui/StarRating';
import { api } from '@/lib/api';
import { formatDateInTimezone } from '@/lib/dateUtils';
import { usePreferences } from '@/hooks/usePreferences';
import type { Newsletter, Feedback, FeedbackType } from '@/types';

// Related mode options for the feedback form
const RELATED_MODES = [
  { value: '', label: 'Select a mode (optional)' },
  { value: 'General Research', label: 'General Research' },
  { value: 'Stock Analysis', label: 'Stock Analysis' },
  { value: 'Strategic Analysis', label: 'Strategic Analysis' },
  { value: 'Portfolio', label: 'Portfolio' },
  { value: 'General / Platform', label: 'General / Platform-wide' },
];

// Feedback category configuration
const FEEDBACK_CATEGORIES: {
  type: FeedbackType;
  label: string;
  icon: React.ReactNode;
  description: string;
  showRating: boolean;
  showNewsletter: boolean;
}[] = [
  {
    type: 'feature_request',
    label: 'Feature Request',
    icon: <Lightbulb className="w-5 h-5" />,
    description: 'Suggest new features or improvements',
    showRating: false,
    showNewsletter: false,
  },
  {
    type: 'bug_report',
    label: 'Bug Report',
    icon: <Bug className="w-5 h-5" />,
    description: 'Report issues or problems',
    showRating: false,
    showNewsletter: false,
  },
  {
    type: 'ui',
    label: 'User Interface',
    icon: <Layout className="w-5 h-5" />,
    description: 'Feedback on design and usability',
    showRating: true,
    showNewsletter: false,
  },
  {
    type: 'billing',
    label: 'Billing',
    icon: <CreditCard className="w-5 h-5" />,
    description: 'Questions or issues with billing',
    showRating: false,
    showNewsletter: false,
  },
  {
    type: 'suggestion',
    label: 'Suggestion',
    icon: <MessageSquare className="w-5 h-5" />,
    description: 'General suggestions and ideas',
    showRating: false,
    showNewsletter: false,
  },
  {
    type: 'newsletter',
    label: 'Newsletter',
    icon: <Mail className="w-5 h-5" />,
    description: 'Rate and review a specific newsletter',
    showRating: true,
    showNewsletter: true,
  },
  {
    type: 'other',
    label: 'Other',
    icon: <HelpCircle className="w-5 h-5" />,
    description: 'Any other feedback',
    showRating: false,
    showNewsletter: false,
  },
];

// Helper to get display label for feedback type
function getFeedbackTypeLabel(type: string): string {
  const category = FEEDBACK_CATEGORIES.find(c => c.type === type);
  return category?.label || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

export function FeedbackPage() {
  const { preferences } = usePreferences();
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [feedbackHistory, setFeedbackHistory] = useState<Feedback[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form state
  const [selectedCategory, setSelectedCategory] = useState<FeedbackType>('feature_request');
  const [selectedNewsletter, setSelectedNewsletter] = useState<string>('');
  const [rating, setRating] = useState(0);
  const [subject, setSubject] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [relatedMode, setRelatedMode] = useState<string>('');

  const currentCategory = FEEDBACK_CATEGORIES.find(c => c.type === selectedCategory)!;
  // Get user's timezone from preferences, fallback to browser timezone
  const userTimezone = preferences?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newsletterData, feedbackData] = await Promise.all([
          api.getNewsletterHistory({ limit: 10 }),
          api.getFeedbackHistory(),
        ]);
        setNewsletters(newsletterData);
        setFeedbackHistory(feedbackData);
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const resetForm = () => {
    setSelectedNewsletter('');
    setRating(0);
    setSubject('');
    setFeedbackText('');
    setRelatedMode('');
  };

  const handleCategoryChange = (type: FeedbackType) => {
    setSelectedCategory(type);
    resetForm();
    setMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation based on category
    if (currentCategory.showNewsletter && !selectedNewsletter) {
      setMessage({ type: 'error', text: 'Please select a newsletter' });
      return;
    }

    if (currentCategory.showRating && rating === 0) {
      setMessage({ type: 'error', text: 'Please provide a rating' });
      return;
    }

    if (!feedbackText.trim()) {
      setMessage({ type: 'error', text: 'Please provide your feedback' });
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      // Prepend related mode to subject if selected
      const finalSubject = relatedMode
        ? `[${relatedMode}] ${subject}`.trim()
        : subject || undefined;

      await api.submitFeedback({
        feedback_type: selectedCategory,
        newsletter_history_id: currentCategory.showNewsletter ? selectedNewsletter : undefined,
        rating: currentCategory.showRating ? rating : undefined,
        subject: finalSubject || undefined,
        feedback_text: feedbackText,
      });
      setMessage({ type: 'success', text: 'Thank you for your feedback!' });
      resetForm();
      // Refresh feedback history
      const updated = await api.getFeedbackHistory();
      setFeedbackHistory(updated);
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to submit feedback' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold">Feedback</h1>
        <p className="text-text-secondary text-sm sm:text-base mt-1">
          Help us improve your experience - we value your feedback!
        </p>
      </div>

      {message && (
        <Alert variant={message.type} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Category Selection */}
      <Card>
        <CardHeader
          title="What would you like to share?"
          description="Select a category for your feedback"
        />

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
          {FEEDBACK_CATEGORIES.map((category) => (
            <button
              key={category.type}
              onClick={() => handleCategoryChange(category.type)}
              className={`p-3 sm:p-4 rounded-lg border-2 transition-all text-left ${
                selectedCategory === category.type
                  ? 'border-primary bg-primary/10'
                  : 'border-border hover:border-primary/50 hover:bg-surface-light'
              }`}
            >
              <div className={`mb-1 sm:mb-2 ${selectedCategory === category.type ? 'text-primary' : 'text-text-secondary'}`}>
                {category.icon}
              </div>
              <div className="font-medium text-xs sm:text-sm">{category.label}</div>
              <div className="text-xs text-text-muted mt-1 line-clamp-2 hidden sm:block">{category.description}</div>
            </button>
          ))}
        </div>
      </Card>

      {/* Feedback Form */}
      <Card>
        <CardHeader
          title={`${currentCategory.label} Feedback`}
          description={currentCategory.description}
        />

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Newsletter Selection (only for newsletter feedback) */}
          {currentCategory.showNewsletter && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Select Newsletter
              </label>
              <select
                value={selectedNewsletter}
                onChange={(e) => setSelectedNewsletter(e.target.value)}
                className="w-full px-4 py-3 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              >
                <option value="">Choose a newsletter...</option>
                {newsletters.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.subject || n.topic || 'Newsletter'} - {formatDateInTimezone(n.generated_at || n.created_at, userTimezone)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Rating (for categories that support it) */}
          {currentCategory.showRating && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Rating
              </label>
              <StarRating rating={rating} onRatingChange={setRating} size="lg" />
            </div>
          )}

          {/* Subject (for non-newsletter feedback) */}
          {!currentCategory.showNewsletter && (
            <Input
              label="Subject (optional)"
              placeholder="Brief summary of your feedback"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          )}

          {/* Related Mode (for non-newsletter feedback) */}
          {!currentCategory.showNewsletter && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Related Mode (optional)
              </label>
              <select
                value={relatedMode}
                onChange={(e) => setRelatedMode(e.target.value)}
                className="w-full px-4 py-3 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              >
                {RELATED_MODES.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <TextArea
            label="Your Feedback"
            placeholder={
              currentCategory.type === 'bug_report'
                ? "Please describe the issue, steps to reproduce, and what you expected to happen..."
                : currentCategory.type === 'feature_request'
                ? "Describe the feature you'd like to see and how it would help you..."
                : currentCategory.type === 'billing'
                ? "Describe your billing question or concern..."
                : "Share your thoughts, suggestions, or experiences..."
            }
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            rows={5}
            required
          />

          <Button type="submit" isLoading={isSubmitting} leftIcon={<Send className="w-4 h-4" />}>
            Submit Feedback
          </Button>
        </form>
      </Card>

      {/* Feedback History */}
      <Card>
        <CardHeader title="Your Feedback History" />

        {feedbackHistory.length === 0 ? (
          <div className="text-center py-6 sm:py-8">
            <MessageSquare className="w-8 h-8 sm:w-10 sm:h-10 text-text-muted mx-auto mb-3" />
            <p className="text-text-secondary text-sm sm:text-base">No feedback submitted yet</p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {feedbackHistory.map((feedback) => (
              <div key={feedback.id} className="p-3 sm:p-4 bg-surface-light rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded">
                      {getFeedbackTypeLabel(feedback.feedback_type || 'general')}
                    </span>
                    {feedback.rating && <StarRating rating={feedback.rating} readonly size="sm" />}
                  </div>
                  <span className="text-text-muted text-xs sm:text-sm">
                    {formatDateInTimezone(feedback.created_at, userTimezone)}
                  </span>
                </div>
                {feedback.subject && (
                  <p className="font-medium text-text-primary text-sm sm:text-base mb-1">{feedback.subject}</p>
                )}
                {(feedback.comment || feedback.feedback_text) && (
                  <p className="text-text-secondary text-xs sm:text-sm">{feedback.comment || feedback.feedback_text}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
